<template>
    <div class="trial-bar" v-if="client && currentBadgeState !== 'activePlan'">
        <div class="trial-bar-content">
            <template v-if="currentBadgeState === 'freeTrial'">
                <p class="trial-text-left">Seu teste acaba em: {{ remainingTimeStr }}</p>
                <p class="trial-text-center">Faça o Upgrade agora mesmo e libere todas as funções Premium.</p>
            </template>

            <template v-else-if="currentBadgeState === 'noPlan'">
                <p class="trial-text-left">Você está na versão gratuita.</p>
                <p class="trial-text-center">Libere todo o potencial da plataforma com o plano Premium.</p>
            </template>

            <button class="upgrade-button" @click="redirectToPlansPage">
                <FontAwesomeIcon :icon="['fas', 'crown']" class="button-icon"/>
                Fazer Upgrade
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import {ref, computed, watch, onMounted, onUnmounted} from 'vue';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {useClient} from '@/hooks/useClient';
import {StarIcon} from "@heroicons/vue/20/solid";
import {library} from '@fortawesome/fontawesome-svg-core';
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';
import {faCrown} from '@fortawesome/free-solid-svg-icons';

library.add(faCrown);
dayjs.extend(duration);

const {client, currentBadgeState} = useClient();
const remainingTimeStr = ref<string>('');
let countdownInterval: number | undefined = undefined;

const calculatedTrialEndDate = computed<dayjs.Dayjs | null>(() => {
    if (client.value?.is_trial && client.value.trial_expires_at) {
        const expiresAtDate = dayjs(client.value.trial_expires_at);
        if (expiresAtDate.isValid()) return expiresAtDate;
        else {
            console.error("Data 'client.trial_expires_at' inválida com dayjs:", client.value.trial_expires_at);
            return null;
        }
    }
    return null;
});

function updateCountdown(): void {
    if (calculatedTrialEndDate.value) {
        const now = dayjs();
        const endDate = calculatedTrialEndDate.value;
        if (endDate.isBefore(now)) {
            remainingTimeStr.value = 'Período de teste esgotado!';
            if (countdownInterval) clearInterval(countdownInterval);
            return;
        }
        const diffMilliseconds = endDate.diff(now);
        const trialDuration = dayjs.duration(diffMilliseconds);
        const days = trialDuration.days();
        const hours = trialDuration.hours();
        const minutes = trialDuration.minutes();
        const seconds = trialDuration.seconds();
        const parts: string[] = [];
        if (days > 0) parts.push(`${days}d`);
        if (hours > 0) parts.push(`${String(hours).padStart(2, '0')}h`);
        if (minutes > 0) parts.push(`${String(minutes).padStart(2, '0')}m`);
        parts.push(`${String(seconds).padStart(2, '0')}s`);
        remainingTimeStr.value = `${parts.join(' ')}`;
    } else {
        remainingTimeStr.value = '';
        if (countdownInterval) clearInterval(countdownInterval);
    }
}

onMounted(() => {
    updateCountdown();
    countdownInterval = window.setInterval(updateCountdown, 1000);
});

onUnmounted(() => {
    if (countdownInterval) clearInterval(countdownInterval);
});


function startOrStopCountdown(): void {
    if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = undefined;
    }
    if (currentBadgeState.value === 'freeTrial' && calculatedTrialEndDate.value && calculatedTrialEndDate.value.isAfter(dayjs())) {
        updateCountdown();
        countdownInterval = setInterval(updateCountdown, 1000) as unknown as number;
    } else if (currentBadgeState.value === 'freeTrial' && calculatedTrialEndDate.value && !calculatedTrialEndDate.value.isAfter(dayjs())) {
        remainingTimeStr.value = 'Período de teste esgotado!';
    } else {
        remainingTimeStr.value = '';
    }
}

watch([() => client.value, calculatedTrialEndDate], startOrStopCountdown, {immediate: true, deep: true});

const badgeBaseClasses = "px-3 py-1.5 rounded-md text-xs sm:text-sm font-semibold flex items-center justify-center shadow-md";

function redirectToPlansPage(): void {
    window.location.href = route('craftable-pro.plans.index');
}

function redirectToPaymentPage(): void {
    window.location.href = 'https://seusite.com.br/pagamento';
}
</script>

<style scoped>
/* Estilos da sua barra */
.trial-bar {
    background-color: #ffc107;
    color: #000;
    padding: 15px 20px;
    width: 100%;
    box-sizing: border-box;
}

.trial-bar-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 1200px;
    margin: 0 auto;
}

.trial-text-left {
    font-weight: bold;
    font-size: 18px;
    margin: 0;
}

.trial-text-center {
    font-size: 16px;
    margin: 0;
}

.upgrade-button {
    background-color: #000;
    color: #fff;
    border: none;
    padding: 10px 20px;
    font-size: 16px;
    font-weight: bold;
    border-radius: 9999px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 1; /* Adicionado para melhor alinhamento vertical */
}

.button-icon {
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
}

@media (max-width: 768px) {
    .trial-bar-content {
        flex-direction: column;
        text-align: center;
    }

    .trial-text-left,
    .trial-text-center,
    .upgrade-button {
        margin-bottom: 10px;
    }
}
</style>
